/* ═══════════════════════════════════════════════════════════════════════════
   LEAD VERTICAL CONFIG — tenant/domain feature flags for the CRM
   ───────────────────────────────────────────────────────────────────────────
   The LeadSystem was originally forked from a DCC (Durham Convention Centre)
   venue-booking CRM. The database schema still carries venue-era columns
   (event_type, event_date, guest_count, preferred_hall, budget_value) which
   are dormant for Step Up Autism but must NOT surface in the UI.

   This file is the single control point for that gating. Renderers import
   the flags and helpers from here — no per-file feature checks, no
   `if (companyName === "…")` scattered across the codebase.

   To add a new tenant/vertical: extend VERTICAL_CONFIG, wire the active
   vertical through app config (env or company settings), and no other
   changes are required.
   ═══════════════════════════════════════════════════════════════════════════ */

import type { LeadStatus, LeadThread } from "./leadApi";

// LeadCustomFields is exported by leadApi.ts but there is a pre-existing
// re-export / dts caching issue in this repo (see LeadCustomFields.tsx —
// same TS2305 with no local cause) that makes named type-import
// unreliable here. Re-declared locally with a matching shape so this
// module is self-contained.
type LeadCustomFieldValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean | null>;
interface LeadCustomField { label: string; value: LeadCustomFieldValue; }
type LeadCustomFields = Record<string, LeadCustomField>;

export type LeadVertical = "autism_services" | "venue" | "tutoring";

export interface LeadVerticalConfig {
  /** Human-readable tenant label used in headers / empty states. */
  label: string;
  /** Prefix used on new lead reference numbers (SUAL260916-001).
   *  Historical rows keep their original prefix — never rewrite. */
  leadNumberPrefix: string;
  /** Venue/event fields that must never surface for this vertical. */
  supportsEventFields: boolean;
  supportsGuestCount: boolean;
  supportsHall: boolean;
  supportsEventBudget: boolean;
  supportsEventDate: boolean;
  /** Column header for the "what did they inquire about?" column. */
  inquirySubjectColumnLabel: string;
  /** Card title inside the Details drawer for the program/event section. */
  inquirySubjectSectionTitle: string;
  /** Display-only overrides for the shared pipeline status keys. Keys in
   *  the DB stay the same across tenants (`ready_for_booking`, etc.); only
   *  the label the user sees is per-vertical. */
  statusLabelOverrides: Partial<Record<LeadStatus, string>>;
}

export const VERTICAL_CONFIG: Record<LeadVertical, LeadVerticalConfig> = {
  autism_services: {
    label: "Step Up Autism Center",
    leadNumberPrefix: "SUAL",
    supportsEventFields: false,
    supportsGuestCount: false,
    supportsHall: false,
    supportsEventBudget: false,
    supportsEventDate: false,
    inquirySubjectColumnLabel: "Program / Inquiry",
    inquirySubjectSectionTitle: "Program / Inquiry Details",
    statusLabelOverrides: {
      new:               "New Inquiry",
      contacted:         "Contacted",
      negotiation:       "Consultation / Follow-up",
      ready_for_quote:   "Assessment / Review",
      ready_for_booking: "Ready to Enroll",
      confirmed:         "Enrolled",
      lost:              "Closed",
    },
  },
  venue: {
    label: "Durham Convention Centre",
    leadNumberPrefix: "DCCL",
    supportsEventFields: true,
    supportsGuestCount: true,
    supportsHall: true,
    supportsEventBudget: true,
    supportsEventDate: true,
    inquirySubjectColumnLabel: "Event",
    inquirySubjectSectionTitle: "Event Details",
    statusLabelOverrides: {},
  },
  tutoring: {
    label: "Step Up Academy",
    leadNumberPrefix: "SUTL",
    supportsEventFields: false,
    supportsGuestCount: false,
    supportsHall: false,
    supportsEventBudget: false,
    supportsEventDate: false,
    inquirySubjectColumnLabel: "Program / Inquiry",
    inquirySubjectSectionTitle: "Program / Inquiry Details",
    statusLabelOverrides: {
      ready_for_booking: "Ready to Enroll",
      confirmed:         "Enrolled",
    },
  },
};

/**
 * Currently-active vertical. Read once at module load. In a real
 * multi-tenant deployment this would come from company settings or an
 * env-backed config route — for the current single-tenant Step Up Autism
 * install a hardcoded default is correct and avoids a synchronous
 * config round-trip on every render.
 */
export const ACTIVE_VERTICAL: LeadVertical =
  (import.meta as any)?.env?.VITE_LEAD_VERTICAL as LeadVertical ?? "autism_services";

export const leadConfig: LeadVerticalConfig = VERTICAL_CONFIG[ACTIVE_VERTICAL];

/* ── Helpers ────────────────────────────────────────────────────────────── */

/**
 * True when a venue/event field should be shown to the current user. Every
 * event / hall / guest_count / budget renderer must gate through this so
 * a future vertical (or DCC tenant switch) toggles the field consistently.
 */
export function showsVenueField(
  field: "event_type" | "event_date" | "guest_count" | "hall" | "budget",
): boolean {
  switch (field) {
    case "event_type": return leadConfig.supportsEventFields;
    case "event_date": return leadConfig.supportsEventDate;
    case "guest_count": return leadConfig.supportsGuestCount;
    case "hall":        return leadConfig.supportsHall;
    case "budget":      return leadConfig.supportsEventBudget;
  }
}

/** Vertical-aware status display label. Falls through to base config's
 *  label for statuses this vertical does not override. */
export function verticalStatusLabel(
  status: LeadStatus,
  baseLabel: string,
): string {
  return leadConfig.statusLabelOverrides[status] ?? baseLabel;
}

/* ── Program / Inquiry subject resolution ──────────────────────────────
 * Autism / tutoring verticals do not have an `event_type` in the domain
 * sense — the inquiry is about a program or service. The backend legacy
 * mapping stuffs the program name into `event_type` for compatibility;
 * this helper reads from custom_fields first and only falls back to the
 * legacy column so we don't render dressed-up venue data.
 * ─────────────────────────────────────────────────────────────────── */

const PROGRAM_KEYS = [
  "program", "program_of_interest", "programOfInterest",
  "service", "interested_service", "interestedService",
  "course", "class",
];

const LOCATION_KEYS = [
  "location", "preferred_location", "preferredLocation", "campus", "site",
];

const CHILD_GRADE_KEYS = [
  "grade", "student_grade", "child_grade", "grade_level", "level",
];

const CHILD_AGE_KEYS = [
  "age", "child_age", "childAge", "student_age",
];

const SCHEDULE_KEYS = [
  "preferred_days", "preferred_schedule", "availability",
  "days", "schedule",
];

const START_KEYS = [
  "preferred_start", "start_date", "preferredStart",
  "start_period", "when", "start",
];

function readCustomField(
  fields: LeadCustomFields | null | undefined,
  keys: string[],
): string | undefined {
  if (!fields) return undefined;
  for (const k of keys) {
    const raw = fields[k]?.value ?? fields[k.toLowerCase()]?.value;
    if (raw == null) continue;
    const s = Array.isArray(raw) ? raw.filter(Boolean).join(", ") : String(raw);
    const trimmed = s.trim();
    if (trimmed !== "") return trimmed;
  }
  return undefined;
}

export interface InquirySubject {
  /** Primary program / service the parent asked about. */
  program: string | undefined;
  /** Location / campus the family is interested in. */
  location: string | undefined;
  /** Child grade or age when supplied. */
  grade: string | undefined;
  age: string | undefined;
  /** Preferred days / availability. */
  schedule: string | undefined;
  /** Preferred start (next term / immediately / etc.). */
  startPeriod: string | undefined;
}

/**
 * Resolve the vertical-appropriate inquiry subject. For autism/tutoring
 * this reads from lead_inquiries.custom_fields with a graceful fallback
 * to the legacy `event_type` column (which the backend maps program→
 * event_type for backwards compatibility with the venue schema). For the
 * venue vertical this still returns event_type as the subject.
 */
export function resolveInquirySubject(lead: LeadThread): InquirySubject {
  // Cast through `any` to sidestep the same LeadCustomFields TS2305
  // baseline issue that affects LeadCustomFields.tsx — the runtime shape
  // is guaranteed by the backend normalizer.
  const inquiry: any = (lead as any).inquiry ?? (lead as any).inquiries?.[0];
  const cf: LeadCustomFields | null | undefined = inquiry?.custom_fields;

  const legacyEvent = lead.event_type
    ? lead.event_type.replace(/_/g, " ")
    : undefined;

  return {
    program:     readCustomField(cf, PROGRAM_KEYS)  ?? (leadConfig.supportsEventFields ? undefined : legacyEvent),
    location:    readCustomField(cf, LOCATION_KEYS),
    grade:       readCustomField(cf, CHILD_GRADE_KEYS),
    age:         readCustomField(cf, CHILD_AGE_KEYS),
    schedule:    readCustomField(cf, SCHEDULE_KEYS),
    startPeriod: readCustomField(cf, START_KEYS),
  };
}

/** Short one-liner suitable for table cell / pipeline card display. */
export function inquirySubjectSummary(lead: LeadThread): string | null {
  const s = resolveInquirySubject(lead);
  const parts = [s.program, s.location].filter(Boolean) as string[];
  return parts.length ? parts.join(" · ") : null;
}

/* ═══════════════════════════════════════════════════════════════════════
   MANUAL CREATE / EDIT — vertical-specific form field descriptors
   ───────────────────────────────────────────────────────────────────────
   Used by CreateLeadDialog, EditLeadDialog, and EmailHub/CreateLeadModal
   so all three surfaces render the same fields for the active vertical.
   No dialog decides its own field list; every one reads `leadFormFields`
   for the active vertical and renders one <TextField /> per entry.

   Autism / tutoring fields are STORED in `lead_inquiries.custom_fields`
   as `{ slug: { label, value } }` — see LeadInquiryNormalizer on the
   backend. They are NEVER written into the venue columns
   (event_type / preferred_hall / guest_count / budget_value) — that
   would revive the very venue-column-abuse this refactor is removing.
   ═══════════════════════════════════════════════════════════════════════ */

export interface LeadFormFieldDef {
  /** Backend key.
   *  - `column:<name>` writes to a native lead_threads column (event_type,
   *    event_date, guest_count, preferred_hall, budget_value). Used only
   *    by the venue vertical.
   *  - `custom:<slug>` writes to lead_inquiries.custom_fields[slug].value
   *    with the corresponding label. Used by autism / tutoring. */
  key: string;
  label: string;
  type: "text" | "textarea" | "number" | "date" | "select";
  /** For type=select; list of `{value,label}` pairs. */
  options?: { value: string; label: string }[];
  /** Column widths follow MUI Grid 12-column layout. */
  gridColumns?: number;
  placeholder?: string;
}

const AUTISM_FORM_FIELDS: LeadFormFieldDef[] = [
  { key: "custom:program",         label: "Program of Interest", type: "text",     gridColumns: 6, placeholder: "e.g. ABA High School Prep" },
  { key: "custom:location",        label: "Preferred Location",  type: "text",     gridColumns: 6 },
  { key: "custom:child_name",      label: "Child Name",          type: "text",     gridColumns: 6 },
  { key: "custom:child_age",       label: "Child Age",           type: "text",     gridColumns: 3, placeholder: "e.g. 8" },
  { key: "custom:grade",           label: "Grade / Level",       type: "text",     gridColumns: 3 },
  { key: "custom:preferred_days",  label: "Preferred Days / Availability", type: "text", gridColumns: 6 },
  { key: "custom:preferred_start", label: "Preferred Start",     type: "text",     gridColumns: 6, placeholder: "e.g. Next term" },
];

const VENUE_FORM_FIELDS: LeadFormFieldDef[] = [
  { key: "column:event_type", label: "Event Type", type: "select", gridColumns: 6, options: [
    { value: "wedding",       label: "Wedding" },
    { value: "corporate",     label: "Corporate" },
    { value: "birthday",      label: "Birthday" },
    { value: "engagement",    label: "Engagement" },
    { value: "mehndi",        label: "Mehndi" },
    { value: "charity_gala",  label: "Charity Gala" },
    { value: "convocation",   label: "Convocation" },
    { value: "baby_shower",   label: "Baby Shower" },
    { value: "anniversary",   label: "Anniversary" },
    { value: "retirement",    label: "Retirement" },
    { value: "holiday_party", label: "Holiday Party" },
    { value: "other",         label: "Other" },
  ] },
  { key: "column:event_date",     label: "Event Date",     type: "date",   gridColumns: 6 },
  { key: "column:guest_count",    label: "Guest Count",    type: "number", gridColumns: 4 },
  { key: "column:budget_value",   label: "Budget ($)",     type: "number", gridColumns: 4 },
  { key: "column:preferred_hall", label: "Preferred Hall", type: "text",   gridColumns: 4 },
];

/** Field definitions for the active vertical's Create / Edit dialogs. */
export const leadFormFields: LeadFormFieldDef[] = leadConfig.supportsEventFields
  ? VENUE_FORM_FIELDS
  : AUTISM_FORM_FIELDS;

/** Section heading used on the create/edit form above the field grid. */
export const leadFormSectionLabel: string = leadConfig.inquirySubjectSectionTitle;

/**
 * Given a `{fieldKey: stringValue}` object as filled by the dialog form,
 * split into `{ columns, customFields }` suitable for a POST /leads
 * payload. Columns are lifted to the top-level (event_type, guest_count,
 * budget_value, budget_known derived). Custom fields are nested under
 * `custom_fields` in the shape the backend normalizer expects:
 * `{ slug: { label, value } }`.
 *
 * Only entries with a non-empty trimmed value are emitted — blank
 * inputs must never mint empty custom_fields rows.
 */
export function splitLeadFormValues(
  values: Record<string, string>,
): { columns: Record<string, any>; customFields: Record<string, { label: string; value: string }> } {
  const columns: Record<string, any> = {};
  const customFields: Record<string, { label: string; value: string }> = {};

  for (const f of leadFormFields) {
    const raw = (values[f.key] ?? "").toString().trim();
    if (raw === "") continue;
    if (f.key.startsWith("column:")) {
      const col = f.key.slice("column:".length);
      if (col === "guest_count")            columns[col] = parseInt(raw, 10);
      else if (col === "budget_value")     { columns[col] = parseFloat(raw); columns.budget_known = true; }
      else                                  columns[col] = raw;
    } else if (f.key.startsWith("custom:")) {
      const slug = f.key.slice("custom:".length);
      customFields[slug] = { label: f.label, value: raw };
    }
  }
  return { columns, customFields };
}

/**
 * Prefill values for the Edit dialog from an existing LeadThread. Reads
 * from custom_fields for `custom:*` keys and directly from columns for
 * `column:*` keys. Empty / missing values become empty strings so the
 * dialog can bind them uncontrolled-safely.
 */
export function loadLeadFormValues(lead: LeadThread): Record<string, string> {
  const cfSrc = (lead as any).inquiry?.custom_fields
             ?? (lead as any).inquiries?.[0]?.custom_fields
             ?? {};
  const values: Record<string, string> = {};
  for (const f of leadFormFields) {
    if (f.key.startsWith("column:")) {
      const col = f.key.slice("column:".length);
      const v = (lead as any)[col];
      values[f.key] = v == null ? "" : String(v);
    } else {
      const slug = f.key.slice("custom:".length);
      const v = cfSrc?.[slug]?.value;
      values[f.key] = v == null ? "" : String(v);
    }
  }
  return values;
}

/* ── Duplicate / merge-reason vocabulary ───────────────────────────────
 * The venue vertical's merge-candidate reasons include `same_event_type`
 * which is meaningless for a service inquiry. Autism / tutoring get a
 * generic vocabulary; venue keeps event_type as an extra signal.
 * ─────────────────────────────────────────────────────────────────── */

const AUTISM_MERGE_REASONS = new Set([
  "same_email", "same_phone", "same_customer", "same_contact",
  "same_child", "similar_name", "similar_inquiry", "recent",
]);

const VENUE_MERGE_REASONS = new Set([
  "same_email", "same_phone", "same_customer", "similar_name",
  "same_event_type", "recent",
]);

export function isMergeReasonAllowed(reason: string): boolean {
  const allowed = leadConfig.supportsEventFields ? VENUE_MERGE_REASONS : AUTISM_MERGE_REASONS;
  return allowed.has(reason);
}

/**
 * True when the "Flag as Same Event" workflow makes sense for this
 * vertical. Autism / tutoring inquiries are for a service, not a bookable
 * event — the workflow reads as "Potential Duplicate" instead (or is
 * hidden entirely if a generic duplicate-flag path isn't wired up yet).
 */
export const supportsSameEventWorkflow: boolean = leadConfig.supportsEventFields;

/** Human-readable duplicate-detection heading used by MergeLeadDialog /
 *  FlagSameEventDialog when they need to reference the workflow name. */
export const duplicateWorkflowLabel: string = leadConfig.supportsEventFields
  ? "Same Event"
  : "Potential Duplicate";
